import { generateSecretKey, getPublicKey } from "nostr-tools/pure";

const STORAGE_KEY = "buzz.cdmsuite.identity.v1";

export interface Identity {
  secretKeyHex: string;
  pubkey: string;
}

function bytesToHex(bytes: Uint8Array): string {
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function fromSecretKey(sk: Uint8Array): Identity {
  return {
    secretKeyHex: bytesToHex(sk),
    pubkey: getPublicKey(sk),
  };
}

export function loadIdentity(): Identity {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as { secretKeyHex?: string };
      if (parsed.secretKeyHex && /^[0-9a-fA-F]{64}$/.test(parsed.secretKeyHex)) {
        return fromSecretKey(hexToBytes(parsed.secretKeyHex));
      }
    } catch {
      // fall through and mint a new identity
    }
  }
  return createIdentity();
}

export function createIdentity(): Identity {
  const identity = fromSecretKey(generateSecretKey());
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ secretKeyHex: identity.secretKeyHex }),
  );
  return identity;
}

export function importIdentity(secretKeyHex: string): Identity {
  const cleaned = secretKeyHex.trim().toLowerCase().replace(/^0x/, "");
  if (!/^[0-9a-f]{64}$/.test(cleaned)) {
    throw new Error("Secret key must be 64 hex characters");
  }
  const identity = fromSecretKey(hexToBytes(cleaned));
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ secretKeyHex: identity.secretKeyHex }),
  );
  return identity;
}

export function secretKeyBytes(identity: Identity): Uint8Array {
  return hexToBytes(identity.secretKeyHex);
}

export function shortPubkey(pubkey: string): string {
  return `${pubkey.slice(0, 8)}…${pubkey.slice(-4)}`;
}
